import { Button } from '@/components/ui/button'
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
} from '@/components/ui/sidebar'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Personal } from './personal'
import { Education } from './education'
import { Contact } from './contact'
import { useForm } from 'react-hook-form'
import { Form } from '@/components/ui/form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAuth } from '@/services/providers/auth-provider'
import { cn } from '@/services/utils/cn'
import { fetchClient } from '@/services/api/client'
import { useToast } from '@/hooks/use-toast'
import { getCookie } from 'cookies-next/client'

const formSchema = z.object({
  phone: z.string().optional().nullable(),
  line_id: z.string().optional().nullable(),
  SocialMedia: z.object({
    ig: z.string().optional().nullable(),
    facebook: z.string().optional().nullable(),
    x: z.string().optional().nullable(),
    tiktok: z.string().optional().nullable(),
  }),
  educational_level: z
    .enum(['PRIMARY', 'MIDDLE', 'HIGH', 'BACHELOR', 'MASTER', 'PHD', 'ANY'])
    .optional()
    .nullable(),
  class_level: z.string().optional().nullable(),
  school_name: z.string().optional().nullable(),
  name_en: z.string().optional().nullable(),
  surname_en: z.string().optional().nullable(),
  birthdate: z.string().optional().nullable(),
  address: z.string().optional().nullable(),
  // purpose
  // categories
})

export type FormSchema = z.infer<typeof formSchema>

export function AppSidebarContent() {
  const auth = useAuth()
  const { toast } = useToast()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name_en: auth?.user?.name_en,
      surname_en: auth?.user?.surname_en,
      birthdate: auth?.user?.birthdate,
      address: auth?.user?.address,
      phone: auth?.user?.phone,
      line_id: auth?.user?.line_id,
      SocialMedia: {
        ig: auth?.user?.SocialMedia?.find((s) => s.platform === 'ig')?.name,
        facebook: auth?.user?.SocialMedia?.find(
          (s) => s.platform === 'facebook'
        )?.name,
        x: auth?.user?.SocialMedia?.find((s) => s.platform === 'x')?.name,
        tiktok: auth?.user?.SocialMedia?.find((s) => s.platform === 'tiktok')
          ?.name,
      },
      educational_level: auth?.user?.educational_level,
      class_level: auth?.user?.class_level,
      school_name: auth?.user?.school_name,
    },
  })

  const onSubmit = async (data: FormSchema) => {
    fetchClient
      .PUT('/user/{id}', {
        params: {
          path: {
            id: String(auth?.user?.id),
          },
        },
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
        body: {
          name_en: data.name_en ?? undefined,
          surname_en: data.surname_en ?? undefined,
          birthdate: data.birthdate ?? undefined,
          address: data.address ?? undefined,
          phone: data.phone ?? undefined,
          line_id: data.line_id ?? undefined,
          educational_level: data.educational_level ?? undefined,
          class_level: data.class_level ?? undefined,
          school_name: data.school_name ?? undefined,
          SocialMedia: [
            { platform: 'ig', name: data.SocialMedia.ig ?? undefined },
            {
              platform: 'facebook',
              name: data.SocialMedia.facebook ?? undefined,
            },
            { platform: 'x', name: data.SocialMedia.x ?? undefined },
            { platform: 'tiktok', name: data.SocialMedia.tiktok ?? undefined },
          ].filter((s) => s.name !== undefined),
        },
      })
      .then((res) => {
        if (res.response.ok) {
          auth.refreshAuth().then(() => {
            form.reset()
          })
          toast({
            title: 'อัปเดตสำเร็จ',
            description: 'อัปเดตข้อมูลสำเร็จแล้ว',
            variant: 'default',
          })
        } else {
          toast({
            title: 'อัปเดตไม่สำเร็จ',
            description: 'อัปเดตข้อมูลไม่สำเร็จ',
            variant: 'destructive',
          })
        }
      })
      .catch(() => {
        toast({
          title: 'อัปเดตไม่สำเร็จ',
          description: 'อัปเดตข้อมูลไม่สำเร็จ',
          variant: 'destructive',
        })
      })
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex h-full flex-col justify-between"
      >
        <SidebarContent>
          <SidebarGroup>
            <Tabs
              defaultValue="personal"
              className="w-full font-darkergrotesque"
            >
              <div className="mx-1 my-6 flex items-center justify-between">
                <TabsList className="rounded-full">
                  <TabsTrigger
                    value="personal"
                    className="rounded-full font-darkergrotesque text-lg"
                  >
                    Personal
                  </TabsTrigger>
                  <TabsTrigger
                    value="education"
                    className="rounded-full font-darkergrotesque text-lg"
                  >
                    Education
                  </TabsTrigger>
                  <TabsTrigger
                    value="contact"
                    className="rounded-full font-darkergrotesque text-lg"
                  >
                    Contact
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent
                value="personal"
                className="custom-tabs-content mx-2 flex flex-col gap-y-2 font-line text-sm"
              >
                <Personal control={form.control} />
              </TabsContent>
              <TabsContent
                value="education"
                className="custom-tabs-content mx-2 flex flex-col gap-y-2 font-line text-sm"
              >
                <Education control={form.control} />
              </TabsContent>
              <TabsContent
                value="contact"
                className="custom-tabs-content mx-2 flex flex-col gap-y-2 font-line text-sm"
              >
                <Contact control={form.control} />
              </TabsContent>
            </Tabs>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <Button
            variant="outline"
            className={cn(
              'rounded-full bg-transparent text-sm font-bold text-zinc-950',
              form.formState.isDirty &&
                'bg-zinc-950 text-white hover:bg-zinc-800 hover:text-white',
              form.formState.isSubmitting && 'opacity-50'
            )}
          >
            Save Preference
          </Button>
        </SidebarFooter>
      </form>
    </Form>
  )
}
